// tour.service.ts
import { Injectable } from '@angular/core';
import {Router, NavigationEnd, NavigationStart} from '@angular/router';
import { BehaviorSubject, Observable, filter } from 'rxjs';
import { Shepherd } from '../shared/shepherd-fix';
import { Store } from "@ngrx/store";
import { advanceStep, setCurrentRoute } from "../state/tour/tour.actions";

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private tour: any;
  private tourActiveSubject = new BehaviorSubject<boolean>(false);
  private currentPageSubject = new BehaviorSubject<string>('');
  private tourCompletedStateKey = 'appTourCompleted';
  private tourContinuationKey = 'tourContinuation';

  // Expose observables to components
  public tourActive$: Observable<boolean> = this.tourActiveSubject.asObservable();
  public currentPage$: Observable<string> = this.currentPageSubject.asObservable();

  // Store tour steps by page
  private tourStepsByPage: { [key: string]: any[] } = {};

  constructor(private router: Router, private store: Store) {
    // Initialize the tour
    this.initializeTour();

    // Set initial page based on current route
    this.currentPageSubject.next(this.getMainRoute(this.router.url));

    // Monitor route changes
    this.setupRouteListener();

    // Set up button click listener for navigation tracking
    this.setupClickListener();
  }

  /**
   * Initialize the Shepherd tour instance
   */
  private initializeTour(): void {
    try {
      this.tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          cancelIcon: { enabled: false },
          classes: 'shepherd-theme-default',
          scrollTo: { behavior: 'auto', block: 'center' },
          // Set default button styling
          buttons: [],
          // Add default popper options for consistent spacing
          popperOptions: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 20] // Default horizontal spacing
                }
              },
              {
                name: 'preventOverflow',
                options: {
                  padding: 10
                }
              }
            ]
          }
        }
      });

      // console.log('Tour initialized successfully');

      // Add event listeners
      this.tour.on('start', this.onTourStart.bind(this));
      this.tour.on('complete', this.onTourComplete.bind(this));
      this.tour.on('cancel', this.onTourCancel.bind(this));
      this.tour.on('show', this.onStepShow.bind(this));
    } catch (error) {
      console.error('Error initializing tour:', error);
      this.createFallbackTour();
    }
  }

  /**
   * Create a fallback tour object when initialization fails
   */
  private createFallbackTour(): void {
    this.tour = {
      on: () => {},
      start: () => {},
      isActive: () => false,
      next: () => {},
      back: () => {},
      cancel: () => {},
      complete: () => {},
      steps: []
    };
  }

  /**
   * Setup route change listener - Updated
   */
  private setupRouteListener(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart) // Use NavigationStart instead of NavigationEnd
    ).subscribe((event: NavigationStart) => {
      // If tour is active when navigation starts, cancel it immediately
      if (this.tour.isActive()) {
        console.log('Navigation started - canceling active tour immediately');
        this.tour.cancel();
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const route = this.getMainRoute(event.urlAfterRedirects);
      const previousRoute = this.currentPageSubject.getValue();

      // Update current page and dispatch to store
      this.currentPageSubject.next(route);
      this.store.dispatch(setCurrentRoute({ route }));

      // Don't automatically continue tour during navigation
      // This prevents showing a tour from one page briefly on another page
      // console.log(`Navigation complete: ${previousRoute} → ${route}`);
    });
  }

  /**
   * Setup click listener for tour navigation between pages
   */
  private setupClickListener(): void {
    setTimeout(() => {
      document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        let navButton = null;

        // Check if clicked element is a navigation link/button
        if (target.tagName === 'A' && target.hasAttribute('routerLink')) {
          navButton = target;
        } else {
          navButton = target.closest('a[routerLink]');
        }

        if (navButton && this.tour.isActive()) {
          const routerLink = navButton.getAttribute('routerLink');
          console.log(`Navigation detected during tour: ${routerLink}`);
          this.setTourContinuation(this.currentPageSubject.getValue(), routerLink);
        }
      });
    }, 1000);
  }

  /**
   * Handler for step show events - updated for better scroll handling
   */
  private onStepShow(evt: any): void {
    const stepIndex = this.tour.steps.indexOf(evt.step);
    if (stepIndex !== -1) {
      this.store.dispatch(advanceStep({ stepIndex }));

      // Find the currently highlighted element
      setTimeout(() => {
        const highlightedElement = document.querySelector('.shepherd-highlighted') as HTMLElement;
        if (highlightedElement) {
          // For dashboard components, remove scroll
          if (highlightedElement.id === 'icp-optimization-section' ||
            this.currentPageSubject.getValue() === '/' ||
            this.currentPageSubject.getValue() === '/dashboard') {

            console.log('Removing scroll from dashboard component');

            // Save original styles to restore later
            this.originalElementStyles = {
              overflow: highlightedElement.style.overflow,
              maxHeight: highlightedElement.style.maxHeight
            };

            // Remove scroll
            highlightedElement.style.overflow = 'visible';
            highlightedElement.style.maxHeight = 'none';
          }
          // For ICP form, we might still want scrolling
          else if (highlightedElement.id === 'icp-optimization-form') {
            // Keep scroll behavior for ICP form
            console.log('Keeping scroll for ICP form');
          }
        }
      }, 100);
    }
  }

  // Add this property to your class
  private originalElementStyles: {overflow?: string, maxHeight?: string} = {};

  /**
   * Register tour steps for a specific page
   */
  registerTourStepsForPage(pageName: string, steps: any[]): void {
    this.tourStepsByPage[pageName] = steps;
  }

  /**
   * Check if tour has been completed
   */
  hasTourBeenCompleted(tourName?: string): boolean {
    if (tourName) {
      return localStorage.getItem(`${this.tourCompletedStateKey}_${tourName}`) === 'true';
    }
    return localStorage.getItem(this.tourCompletedStateKey) === 'true';
  }

  /**
   * Reset the tour completion status
   */
  resetTourStatus(tourName?: string): void {
    if (tourName) {
      localStorage.removeItem(`${this.tourCompletedStateKey}_${tourName}`);
    } else {
      localStorage.removeItem(this.tourCompletedStateKey);
    }
  }

  /**
   * Start the tour on the current page
   */
  startTour(tourName?: string, forcedRoute?: string): void {
    // CRITICAL DOUBLE-CHECK: Do not start tour if already completed
    const currentPage = this.currentPageSubject.getValue();

    // Check for dashboard tour
    if ((currentPage === '/' || currentPage === '/dashboard') &&
      localStorage.getItem('salesommo_tour_completed_dashboard') === 'true') {
      console.log('Dashboard tour already completed, not starting (TourService check)');
      return;
    }

    // Check for ICP tour
    if (currentPage === '/icp-optimization' &&
      localStorage.getItem('salesommo_tour_completed_icp') === 'true') {
      console.log('ICP tour already completed, not starting (TourService check)');
      return;
    }

    // Don't start if already active
    if (this.tour.isActive()) {
      console.log('Tour already active, not starting again');
      return;
    }

    // Rest of your existing logic...
    this.clearTourSteps();
    console.log(`Starting tour for ${tourName || 'default'}, current route: ${this.currentPageSubject.getValue()}`);
    this.showStepsForCurrentPage();

    if (this.tour.steps && this.tour.steps.length > 0) {
      console.log(`Starting tour with ${this.tour.steps.length} steps`);
      this.tour.start();
    } else {
      console.warn('No tour steps found for page:', this.currentPageSubject.getValue());
    }
  }

  /**
   * Show tour steps for the current page
   */
  showStepsForCurrentPage(): void {
    // Clear existing steps
    this.clearTourSteps();

    // Get steps for current page
    const currentPage = this.currentPageSubject.getValue();
    console.log(`Looking for steps for page: ${currentPage}`);
    const steps = this.tourStepsByPage[currentPage];

    if (steps && steps.length > 0) {
      console.log(`Found ${steps.length} steps for ${currentPage}`);

      // Add steps with enhancements
      steps.forEach(step => {
        // Clone the step to avoid modifying original
        const enhancedStep = { ...step };

        // Add element existence check if not already present
        if (enhancedStep.attachTo?.element && !enhancedStep.beforeShowPromise) {
          enhancedStep.beforeShowPromise = () => {
            return new Promise<void>((resolve) => {
              const checkElement = () => {
                const element = document.querySelector(enhancedStep.attachTo.element);
                if (element) {
                  console.log(`Element ${enhancedStep.attachTo.element} found, showing step`);
                  resolve();
                } else {
                  console.log(`Waiting for element ${enhancedStep.attachTo.element}...`);
                  setTimeout(checkElement, 200);
                }
              };
              checkElement();
            });
          };
        }

        // Add the step
        this.tour.addStep(enhancedStep);
      });
    } else {
      console.warn('No tour steps found for page:', currentPage);
    }
  }

  /**
   * Add a step with click handling for automatic advancement
   */
  private addStepWithClickHandling(stepConfig: any): void {
    // Clone the step config
    const step = { ...stepConfig };

    // Ensure we use faster animation
    if (!step.when) {
      step.when = {};
    }

    // Add show event handler for faster animations
    const originalBefore = step.when.before;
    step.when.before = {
      show: function() {
        // Add a class to get faster transitions
        document.body.classList.add('tour-fast-transitions');

        // Call original handler if it exists
        if (originalBefore && originalBefore.show) {
          originalBefore.show.apply(this, arguments);
        }
      }
    };

    // Add the step to the tour
    this.tour.addStep(step);
  }

  /**
   * Setup click listener on highlighted element
   */
  private setupElementClickListener(selector: string): void {
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        const clickHandler = () => {
          if (this.tour.isActive()) {
            if (this.tour.steps.indexOf(this.tour.currentStep) === this.tour.steps.length - 1) {
              this.tour.complete();
            } else {
              this.tour.next();
            }
          }
          element.removeEventListener('click', clickHandler);
        };
        element.addEventListener('click', clickHandler);
      }
    }, 100);
  }

  /**
   * Clear all steps from the tour
   */
  private clearTourSteps(): void {
    if (this.tour.steps) {
      while (this.tour.steps.length > 0) {
        this.tour.removeStep(this.tour.steps[0].id);
      }
    }
  }

  /**
   * Called when a tour starts
   */
  private onTourStart(): void {
    this.tourActiveSubject.next(true);

    // Find ALL potential overlay elements and change their color
    setTimeout(() => {
      // Try different selectors that might be the overlay
      const overlaySelectors = [
        '.shepherd-modal-overlay-container',
        '.shepherd-modal-overlay',
        '.shepherd-overlay',
        'body.shepherd-modal-is-visible::before'
      ];

      // Try to find and modify each potential overlay element
      overlaySelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          if (element instanceof HTMLElement) {
            console.log(`Found potential overlay element: ${selector}`);
            element.style.backgroundColor = 'rgba(255, 0, 0, 0.7)';
          }
        });
      });

      // As a last resort, try to add a custom overlay
      /*
      if (!document.querySelector('.custom-tour-overlay')) {
        const customOverlay = document.createElement('div');
        customOverlay.className = 'custom-tour-overlay';
        customOverlay.style.position = 'fixed';
        customOverlay.style.top = '0';
        customOverlay.style.left = '0';
        customOverlay.style.width = '100vw';
        customOverlay.style.height = '100vh';
        customOverlay.style.backgroundColor = '#0f174b';
        customOverlay.style.opacity = '0.5';
        customOverlay.style.zIndex = '9997'
        customOverlay.style.backdropFilter = `blur(${12}px)`;
        document.body.appendChild(customOverlay);
      }
      */
    }, 100);
  }

  /**
   * Called when a tour is completed
   */
  private onTourComplete(): void {
    this.tourActiveSubject.next(false);
    localStorage.setItem(this.tourCompletedStateKey, 'true');
    this.clearTourContinuation();

    // Remove custom overlay
    const customOverlay = document.querySelector('.custom-tour-overlay');
    if (customOverlay && customOverlay.parentNode) {
      customOverlay.parentNode.removeChild(customOverlay);
    }

    // Restore original styles
    this.restoreElementStyles();
  }

  // Add this method to restore styles
  private restoreElementStyles(): void {
    // Find the last highlighted element
    const highlightedElement = document.querySelector('.shepherd-highlighted') as HTMLElement;
    if (highlightedElement && this.originalElementStyles) {
      // Restore original styles
      if (this.originalElementStyles.overflow) {
        highlightedElement.style.overflow = this.originalElementStyles.overflow;
      }
      if (this.originalElementStyles.maxHeight) {
        highlightedElement.style.maxHeight = this.originalElementStyles.maxHeight;
      }
    }
  }

  /**
   * Called when a tour is cancelled
   */
  private onTourCancel(): void {
    console.log('Tour cancel event triggered');
    this.tourActiveSubject.next(false);
    this.clearTourContinuation();

    // Remove custom overlay
    const customOverlay = document.querySelector('.custom-tour-overlay');
    if (customOverlay && customOverlay.parentNode) {
      customOverlay.parentNode.removeChild(customOverlay);
    }

    // Do manual cleanup to ensure everything is removed
    this.manualCleanup();
  }

  // Add a property to store scroll position if not already present
  private scrollPosition: number = 0;

  /**
   * Cancel the current tour with enhanced cleanup
   */
  cancelTour(): void {
    console.log('Canceling tour with enhanced cleanup');

    // Cancel the tour if active
    if (this.tour && this.tour.isActive && this.tour.isActive()) {
      this.tour.cancel();
    }

    // Do manual cleanup in case the tour's cancel event doesn't fire properly
    this.manualCleanup();
  }

  /**
   * Manual cleanup for tour elements
   */
  private manualCleanup(): void {
    // Remove modal class from body
    document.body.classList.remove('shepherd-modal-is-visible');

    // Clean up overlay elements
    const overlays = document.querySelectorAll('.shepherd-modal-overlay-container');
    overlays.forEach(overlay => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });

    // Remove any highlighted elements
    const highlightedElements = document.querySelectorAll('.shepherd-highlighted');
    highlightedElements.forEach(element => {
      element.classList.remove('shepherd-highlighted');
    });

    // Reset body scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    // Restore scroll position if we saved it
    if (this.scrollPosition) {
      window.scrollTo(0, this.scrollPosition);
    }

    console.log('Manual cleanup completed');
  }

  /**
   * Store continuation data for cross-page tours
   */
  setTourContinuation(fromRoute: string, toRoute: string): void {
    localStorage.setItem(this.tourContinuationKey, JSON.stringify({
      from: fromRoute,
      to: toRoute,
      timestamp: Date.now()
    }));
  }

  /**
   * Check if we should continue a tour
   */
  shouldContinueTour(): boolean {
    // First check traditional continuation
    const continuationStr = localStorage.getItem(this.tourContinuationKey);
    if (continuationStr) {
      try {
        const continuation = JSON.parse(continuationStr);
        const currentRoute = this.currentPageSubject.getValue();
        const isRecent = (Date.now() - continuation.timestamp) < 30000; // Extend to 30 seconds

        // Check if this is a continuation to our current route
        if (continuation.to === currentRoute && isRecent) {
          console.log('Found continuation to current route:', currentRoute);
          return true;
        }
      } catch (e) {
        return false;
      }
    }

    // If no traditional continuation, check if there's a tour in progress
    // that should be restored after refresh
    try {
      // Check for a saved tour state in localStorage
      const tourStateStr = localStorage.getItem('salesommo_tour_state');
      if (tourStateStr) {
        const tourState = JSON.parse(tourStateStr);
        const currentRoute = this.currentPageSubject.getValue();
        const isTourRecent = (Date.now() - tourState.timestamp) < 3600000; // 1 hour

        if (tourState.route === currentRoute && isTourRecent) {
          console.log('Found saved tour state for current route after refresh');
          return true;
        }
      }
    } catch (e) {
      console.error('Error checking saved tour state:', e);
    }

    return false;
  }

  /**
   * Clear continuation data
   */
  clearTourContinuation(): void {
    localStorage.removeItem(this.tourContinuationKey);
  }

  /**
   * Get main route without query params
   */
  private getMainRoute(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  /**
   * Check if a tour is currently active
   */
  isActiveTour(): boolean {
    return this.tour && this.tour.isActive ? this.tour.isActive() : false;
  }

  /**
   * Get tour instance (for advanced usage)
   */
  getTour(): any {
    return this.tour;
  }

  /**
   * Debug method to log tour state
   */
  logTourState(): void {
    const currentPage = this.currentPageSubject.getValue();
    console.log('Tour state:', {
      currentPage,
      isActive: this.isActiveTour(),
      hasSteps: this.hasStepsForCurrentPage(),
      availablePages: Object.keys(this.tourStepsByPage)
    });
  }

  /**
   * Check if current page has steps
   */
  private hasStepsForCurrentPage(): boolean {
    const currentPage = this.currentPageSubject.getValue();
    return !!this.tourStepsByPage[currentPage]?.length;
  }

  /**
   * Debug the current tour state
   */
  public debugTourState(): void {
    console.group('Tour Service Debug Information');

    // Check basic state
    console.log('Tour active:', this.isActiveTour());
    console.log('Current page:', this.currentPageSubject.getValue());

    // Check registered steps
    console.log('Available pages with steps:', Object.keys(this.tourStepsByPage));

    const currentPage = this.currentPageSubject.getValue();
    const stepsForCurrentPage = this.tourStepsByPage[currentPage] || [];
    console.log(`Steps for current page (${currentPage}):`, stepsForCurrentPage.length);

    // Check current steps in the tour
    console.log('Steps currently in tour:', this.tour.steps ? this.tour.steps.length : 0);

    // Check tour objects
    console.log('Tour object initialized:', !!this.tour);
    console.log('Tour has start method:', !!this.tour.start);

    // Check tour settings
    if (this.tour && this.tour.options) {
      console.log('Tour options:', this.tour.options);
    }

    // Log element existence
    if (currentPage === '/icp-optimization') {
      const element = document.querySelector('#icp-optimization-form');
      console.log('ICP form element exists:', !!element);
      if (element) {

        /*
        console.log('Element visible:', element.offsetParent !== null);
        console.log('Element dimensions:', {
          width: element.offsetWidth,
          height: element.offsetHeight
        });
        */

      }
    }

    console.groupEnd();
  }

  /**
   * Check if we're continuing a tour from another page
   */
  isContinuingTour(): boolean {
    // Check if we recently navigated from ICP to dashboard
    const continuationStr = localStorage.getItem('salesommo_tour_continuation');
    if (!continuationStr) return false;

    try {
      const continuation = JSON.parse(continuationStr);
      const isRecent = (Date.now() - continuation.timestamp) < 10000; // within last 10 seconds
      const comingFromIcp = continuation.from === '/icp-optimization';
      const goingToDashboard = continuation.to === '/' || continuation.to === '/dashboard';

      return isRecent && comingFromIcp && goingToDashboard;
    } catch (e) {
      return false;
    }
  }

}
